import React, { useState, useRef } from 'react';
import { Field, Label } from '../Catalyst/fieldset';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ClipLoader } from 'react-spinners';
import { standardInputContainerClasses } from './inputStyles';

const TagInput = ({
    name,
    label,
    options = [],
    defaultValue = [],
    placeholder,
    error,
    requiredFlag,
    onAddItem,
    onChange,
    loading = false,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedValues, setSelectedValues] = useState(defaultValue);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);

    const filteredOptions = options.filter(
        opt => !selectedValues.includes(opt.value) &&
               opt.text.toLowerCase().includes(inputValue.toLowerCase())
    );

    const handleAddTag = (value, text) => {
        const newValues = [...selectedValues, value];
        setSelectedValues(newValues);
        setInputValue('');
        setShowDropdown(false);
        onChange(null, { name, value: newValues });
    };

    const handleRemoveTag = (value) => {
        const newValues = selectedValues.filter(v => v !== value);
        setSelectedValues(newValues);
        onChange(null, { name, value: newValues });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            // Check if it's a new item or existing option
            const existingOption = options.find(
                opt => opt.text.toLowerCase() === inputValue.toLowerCase()
            );
            if (existingOption && !selectedValues.includes(existingOption.value)) {
                handleAddTag(existingOption.value, existingOption.text);
            } else if (!existingOption && onAddItem) {
                // Allow adding new items
                const newValue = inputValue.trim();
                handleAddTag(newValue, newValue);
            }
        } else if (e.key === 'Backspace' && !inputValue && selectedValues.length > 0) {
            handleRemoveTag(selectedValues[selectedValues.length - 1]);
        }
    };

    const getTagLabel = (value) => {
        const option = options.find(opt => opt.value === value);
        return option ? option.text : value;
    };

    return (
        <Field>
            <Label>
                {label}
                {requiredFlag && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
            </Label>
            <div className="relative mt-1">
                <div
                    className={clsx(
                        'flex flex-wrap gap-1 p-1.5',
                        standardInputContainerClasses,
                        error && 'border-red-500 dark:border-red-600'
                    )}
                    onClick={() => inputRef.current?.focus()}
                >
                    {selectedValues.map(value => (
                        <span
                            key={value}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface-inset dark:bg-zinc-700 text-sm"
                        >
                            {getTagLabel(value)}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTag(value);
                                }}
                                className="hover:text-red-500 dark:hover:text-red-400"
                            >
                                <XMarkIcon className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        onKeyDown={handleKeyDown}
                        placeholder={selectedValues.length === 0 ? placeholder : ''}
                        className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm py-0.5"
                    />
                    {loading && (
                        <div className="absolute right-3 top-1/2 -translate-y-[45%] z-50 pointer-events-none bg-surface">
                            <ClipLoader color="#6366f1" size={18} />
                        </div>
                    )}
                </div>
                {showDropdown && filteredOptions.length > 0 && (
                    <ul className="absolute z-10 w-full mt-1 bg-surface dark:bg-zinc-800 border border-border dark:border-zinc-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredOptions.map(option => (
                            <li
                                key={option.value}
                                onClick={() => handleAddTag(option.value, option.text)}
                                className="px-3 py-2 hover:bg-surface-hover dark:hover:bg-zinc-700 cursor-pointer text-sm"
                            >
                                {option.text}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {error && <p className="text-sm mt-1 text-text-error">{error}</p>}
        </Field>
    );
}

export default TagInput;